import {
  useState,
  useRef,
  useCallback,
  useLayoutEffect,
  useEffect,
} from "react";
import ReactDOM from "react-dom";

/**
 * Manages the multiple-duplicates flyout UI used on scheduler event card menus.
 * Mirrors the Daily view flyout: counter + Duplicate action rendered beside the menu.
 *
 * @param {Object} options
 * @param {React.RefObject} [options.scrollContainerRef] - Optional scroll container for flyout bounds.
 * @param {Object} options.menuStyles - Scheduler.module.scss styles object.
 * @param {string} [options.dropdownToggleSelector='.interactive-dropdown'] - Selector for menu toggle clicks.
 */
export function useMultipleDuplicatesFlyout({
  scrollContainerRef,
  menuStyles,
  dropdownToggleSelector = ".interactive-dropdown",
}) {
  const duplicateFlyoutRef = useRef(null);
  const [openEventMenuKey, setOpenEventMenuKey] = useState("");
  const [duplicateMenuKey, setDuplicateMenuKey] = useState("");
  const [duplicateCount, setDuplicateCount] = useState(1);
  const [duplicateMenuItemEl, setDuplicateMenuItemEl] = useState(null);
  const [duplicateEventContext, setDuplicateEventContext] = useState(null);
  const [duplicateFlyoutPos, setDuplicateFlyoutPos] = useState({
    top: 0,
    left: 0,
    visibility: "hidden",
  });

  const flyoutClassName = menuStyles["multiple-duplicates-flyout"];

  const isDuplicateFlyoutTarget = useCallback(
    (target) => {
      if (!target) return false;

      return (
        duplicateFlyoutRef.current?.contains(target) ||
        Boolean(target.closest?.(`.${flyoutClassName}`))
      );
    },
    [flyoutClassName],
  );

  const resetDuplicateFlyoutState = useCallback(() => {
    setDuplicateMenuKey("");
    setDuplicateCount(1);
    setDuplicateMenuItemEl(null);
    setDuplicateEventContext(null);
    setDuplicateFlyoutPos({ top: 0, left: 0, visibility: "hidden" });
  }, []);

  const closeEventMenuAndFlyout = useCallback(() => {
    resetDuplicateFlyoutState();
    setOpenEventMenuKey("");
  }, [resetDuplicateFlyoutState]);

  const updateDuplicateFlyoutPosition = useCallback(() => {
    if (!duplicateMenuItemEl) return;

    if (!document.body.contains(duplicateMenuItemEl)) {
      return;
    }

    const flyoutEl = duplicateFlyoutRef.current;
    const flyoutWidth = flyoutEl?.offsetWidth || 180;
    const flyoutHeight = flyoutEl?.offsetHeight || 44;
    const viewportPadding = 8;
    const flyoutGap = 0;

    const menuItemRect = duplicateMenuItemEl.getBoundingClientRect();
    const dropdownMenu = duplicateMenuItemEl.closest(
      `.${menuStyles["office-dropdown-menu"]}`,
    );
    const menuRect = dropdownMenu?.getBoundingClientRect();

    const bounds = {
      left: viewportPadding,
      top: viewportPadding,
      right: window.innerWidth - viewportPadding,
      bottom: window.innerHeight - viewportPadding,
    };

    const scrollContainer = scrollContainerRef?.current;
    if (scrollContainer) {
      const containerRect = scrollContainer.getBoundingClientRect();
      bounds.left = Math.max(bounds.left, containerRect.left + viewportPadding);
      bounds.top = Math.max(bounds.top, containerRect.top + viewportPadding);
      bounds.right = Math.min(
        bounds.right,
        containerRect.right - viewportPadding,
      );
      bounds.bottom = Math.min(
        bounds.bottom,
        containerRect.bottom - viewportPadding,
      );
    }

    const anchorLeft = menuRect?.left ?? menuItemRect.left;
    const anchorRight = menuRect?.right ?? menuItemRect.right;
    const spaceOnRight = bounds.right - anchorRight - flyoutGap;
    const spaceOnLeft = anchorLeft - bounds.left - flyoutGap;

    let left;
    if (spaceOnRight >= flyoutWidth) {
      left = anchorRight + flyoutGap;
    } else if (spaceOnLeft >= flyoutWidth) {
      left = anchorLeft - flyoutWidth - flyoutGap;
    } else if (spaceOnRight >= spaceOnLeft) {
      left = Math.max(bounds.left, bounds.right - flyoutWidth);
    } else {
      left = bounds.left;
    }

    left = Math.max(bounds.left, Math.min(left, bounds.right - flyoutWidth));

    const halfHeight = flyoutHeight / 2;
    let top = menuItemRect.top + menuItemRect.height / 2;
    top = Math.max(
      bounds.top + halfHeight,
      Math.min(top, bounds.bottom - halfHeight),
    );

    setDuplicateFlyoutPos({
      top,
      left,
      visibility: "visible",
    });
  }, [duplicateMenuItemEl, menuStyles, scrollContainerRef]);

  useLayoutEffect(() => {
    if (!duplicateMenuKey || !duplicateMenuItemEl) return;

    updateDuplicateFlyoutPosition();
    const rafId = requestAnimationFrame(updateDuplicateFlyoutPosition);

    const handleReposition = () => {
      requestAnimationFrame(updateDuplicateFlyoutPosition);
    };

    window.addEventListener("resize", handleReposition);
    window.addEventListener("scroll", handleReposition, true);

    return () => {
      cancelAnimationFrame(rafId);
      window.removeEventListener("resize", handleReposition);
      window.removeEventListener("scroll", handleReposition, true);
    };
  }, [duplicateMenuKey, duplicateMenuItemEl, updateDuplicateFlyoutPosition]);

  useEffect(() => {
    if (!duplicateMenuKey && !openEventMenuKey) return;

    const handleClickOutside = (event) => {
      // Let the dropdown toggle handler own open/close so a second click
      // on the three-dot icon can close the menu instead of reopening it.
      if (
        isDuplicateFlyoutTarget(event.target) ||
        event.target.closest(`.${menuStyles["office-dropdown-menu"]}`) ||
        event.target.closest(dropdownToggleSelector)
      ) {
        return;
      }

      closeEventMenuAndFlyout();
    };

    document.addEventListener("mousedown", handleClickOutside, true);

    return () => {
      document.removeEventListener("mousedown", handleClickOutside, true);
    };
  }, [
    duplicateMenuKey,
    openEventMenuKey,
    isDuplicateFlyoutTarget,
    closeEventMenuAndFlyout,
    menuStyles,
    dropdownToggleSelector,
  ]);

  const createEventMenuToggleHandler = useCallback(
    (eventMenuKey) => (e) => {
      if (isDuplicateFlyoutTarget(e?.target)) {
        return;
      }

      // Selecting any menu option (or dismissing the menu with Esc/Tab) closes
      // the card menu along with the multiple-duplicates flyout.
      if (e?.target?.closest?.(`.${menuStyles["office-dropdown-menu"]}`)) {
        closeEventMenuAndFlyout();
        return;
      }

      const clickedEventMenuToggle = e?.target?.closest?.(
        dropdownToggleSelector,
      );
      if (!clickedEventMenuToggle) {
        return;
      }

      if (openEventMenuKey === eventMenuKey) {
        closeEventMenuAndFlyout();
        return;
      }

      if (duplicateMenuKey) {
        resetDuplicateFlyoutState();
      }
      setOpenEventMenuKey(eventMenuKey);
    },
    [
      isDuplicateFlyoutTarget,
      menuStyles,
      dropdownToggleSelector,
      openEventMenuKey,
      closeEventMenuAndFlyout,
      duplicateMenuKey,
      resetDuplicateFlyoutState,
    ],
  );

  const handleMultipleDuplicatesClick = useCallback(
    (eventMenuKey, event, requestedDate) => (e) => {
      e.preventDefault();
      e.stopPropagation();
      setOpenEventMenuKey(eventMenuKey);
      setDuplicateMenuKey(eventMenuKey);
      setDuplicateEventContext({ event, requestedDate });
      setDuplicateMenuItemEl(e.currentTarget);
      setDuplicateCount(1);
    },
    [],
  );

  const renderMultipleDuplicatesFlyout = useCallback(
    (onDuplicate, t) => {
      if (!duplicateMenuKey || !duplicateEventContext) return null;

      return ReactDOM.createPortal(
        <div
          ref={duplicateFlyoutRef}
          className={menuStyles["multiple-duplicates-flyout"]}
          style={{
            position: "fixed",
            top: duplicateFlyoutPos.top,
            left: duplicateFlyoutPos.left,
            transform: "translateY(-50%)",
            visibility: duplicateFlyoutPos.visibility,
            zIndex: 1060,
          }}
          onClick={(e) => e.stopPropagation()}
          onMouseDown={(e) => {
            e.preventDefault();
            e.stopPropagation();
          }}
        >
          <div className={menuStyles["multiple-duplicates-counter"]}>
            <button
              type="button"
              className={menuStyles["multiple-duplicates-btn"]}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                setDuplicateCount((prev) => Math.max(1, prev - 1));
              }}
            >
              -
            </button>
            <span className={menuStyles["multiple-duplicates-count"]}>
              {duplicateCount}
            </span>
            <button
              type="button"
              className={menuStyles["multiple-duplicates-btn"]}
              onMouseDown={(e) => {
                e.preventDefault();
                e.stopPropagation();
              }}
              onClick={(e) => {
                e.stopPropagation();
                setDuplicateCount((prev) => prev + 1);
              }}
            >
              +
            </button>
          </div>
          <button
            type="button"
            className={menuStyles["multiple-duplicates-action"]}
            onMouseDown={(e) => {
              e.preventDefault();
              e.stopPropagation();
            }}
            onClick={(e) => {
              e.stopPropagation();
              const { event: contextEvent, requestedDate } =
                duplicateEventContext;
              const requestedCount = duplicateCount;
              closeEventMenuAndFlyout();
              onDuplicate(contextEvent, requestedDate, requestedCount);
            }}
          >
            {t("scheduler.duplicatePlain") || "Duplicate"}
          </button>
        </div>,
        document.body,
      );
    },
    [
      duplicateMenuKey,
      duplicateEventContext,
      duplicateCount,
      duplicateFlyoutPos,
      menuStyles,
      closeEventMenuAndFlyout,
    ],
  );

  return {
    openEventMenuKey,
    closeEventMenuAndFlyout,
    createEventMenuToggleHandler,
    handleMultipleDuplicatesClick,
    renderMultipleDuplicatesFlyout,
  };
}
